/**
 * 서비스지역 / 주문 API 클라이언트.
 *
 * Base URL: `VITE_API_BASE_URL` (mobile API host) — `/service-area/...`, `/order/api/v2/...` 모두 같은 host.
 *
 * 인증 헤더 (혼재):
 *  1. 시스템(`x-api-key`, `x-biz-code`): 빌드타임 env, instance 헤더로 박힘 (광역권역 API 전용)
 *  2. 사용자(`Authorization: Bearer ${token}`): tokenStore에서 가져와 인터셉터 자동 주입 (waitlist, availability)
 *
 * 비회원 진입 시 token=null → Authorization 헤더 미주입 → 시스템 인증만 동작 (광역권역 조회 OK).
 *
 * 응답 envelope: { txid, status, message, data, error? }
 *  - status < 400: data unwrap → 호출 측 반환
 *  - status >= 400: error.code/description 추출해 ApiError throw
 */

import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'

import { API_BASE_URL, API_KEY, API_TIMEOUT_MS, BIZ_CODE } from '@/constants'
import { ApiError, type ApiResponse } from '@/types/api'

import { getToken } from './tokenStore'

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    'x-biz-code': BIZ_CODE,
  },
})

export interface HttpClient extends AxiosInstance {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  post<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  put<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  patch<T = unknown, B = unknown>(url: string, data?: B, config?: AxiosRequestConfig): Promise<T>
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
}

const api: HttpClient = axiosInstance

/** Bearer 토큰 자동 주입 — token 없으면 헤더 미설정 (비회원 모드) */
api.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse<unknown>
    if (typeof body.status === 'number' && body.status >= 400) {
      throw new ApiError(body.error?.code ?? '', body.error?.description ?? null, body.message, body.status, body.txid)
    }
    return body.data as never
  },
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as ApiResponse<unknown> | undefined
      if (body) {
        return Promise.reject(
          new ApiError(
            body.error?.code ?? '',
            body.error?.description ?? null,
            body.message ?? error.message,
            body.status ?? error.response.status,
            body.txid ?? ''
          )
        )
      }
    }
    return Promise.reject(error)
  }
)

export default api
